import { useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '../store/store';
import { AIDrawerContext } from '../providers/AIDrawerContext';
import { selectActiveBlock } from '../slices/blocks/blocksSlice';
import { selectTodaySession } from '../slices/todaySession/todaySessionSlice';
import { selectCompletedVideosCount } from '../slices/videos/videosSlice';
import {
  selectIsStandaloneStudy,
  selectStudyContext,
  enterStandalone,
  exitStandalone,
  setReturnTarget,
} from '../slices/studyContext/studyContextSlice';
import { returnFromStudySet } from '../services/studySets';
import { selectConversation } from '../slices/chat/chatSelectors';
import {
  captureStudySetEvent,
  studySetEventProps,
} from '../components/ora/Chat/Companion/blocks/studySetAnalytics';
import { resolveStudySetReturn } from './studySetReturn';
import type { StudyReturnTo } from '../slices/studyContext/studyContextSlice';
import { resetActiveQuestionIndex } from '../helpers/studyHelpers';
import { examsSelectors } from '../slices/exams/examsSlice';
import { isPreclinicalModeExam } from '../utils/examUtils';
import { selectSessionFlashcardCompletedCount } from '../slices/flashcardRun/flashcardRunSlice';
import { selectQbankProgressForSession } from '../slices/qbankProgress/qbankProgressSlice';
import { BlockType } from '../types/Study';

const routes = {
  // "Home" for study returns means the Dashboard, not the AI desktop at /.
  home: '/dashboard',
  qbankStart: '/qbank-start',
  topics: '/topics',
  flashcardDecks: '/flashcards',
  qbankBlock: '/study/qbank/block',
  qbankReview: '/study/qbank/block/review',
  flashcards: '/study/flashcards',
  video: '/study/video',
  summary: '/study/summary',
  selfAssessments: '/self-assessments',
};

type StudyFlowEvent =
  | { type: 'START_QBANK_BLOCK'; blockId?: string; preloaded?: boolean }
  | { type: 'REVIEW_QBANK'; blockId?: string; preloaded?: boolean }
  | { type: 'REVIEW_QBANK_BLOCK'; blockId?: string; preloaded?: boolean }
  | { type: 'PAUSE_BLOCK' }
  | { type: 'END_QBANK_REVIEW' }
  | { type: 'START_FLASHCARDS'; search?: string }
  | { type: 'FINISH_FLASHCARDS' }
  | { type: 'START_SESSION_GENERIC' }
  | { type: 'RETURN_TO_HOME' }
  | { type: 'START_VIDEOS' }
  | { type: 'START_MULTI_LECTURE_FLASHCARDS'; lectureIds: string[] }
  | {
      type: 'START_MULTI_LECTURE_VIDEOS';
      lectureIds: string[];
      returnTo?: StudyReturnTo;
      returnPath?: string | null;
    };

const buildSearch = (params: Record<string, string | undefined>) => {
  const search = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value) {
      search.set(key, value);
    }
  });

  const query = search.toString();
  return query ? `?${query}` : '';
};

const buildLectureSearch = (lectureIds?: string[]) =>
  lectureIds && lectureIds.length > 0
    ? buildSearch({ lectureIds: lectureIds.join(',') })
    : '';

export const useStudyFlow = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const activeBlock = useSelector(selectActiveBlock);
  const todaySession = useSelector(selectTodaySession);

  const qbankProgress = useSelector((state: RootState) =>
    selectQbankProgressForSession(state, todaySession?.id)
  );

  const primaryExam = useSelector(examsSelectors.selectPrimaryExam);

  const completedFlashcardsCount = useSelector(
    selectSessionFlashcardCompletedCount
  );

  const completedVideosCount = useSelector((state: RootState) =>
    todaySession?.id ? selectCompletedVideosCount(state, todaySession.id) : 0
  );

  const isStandaloneStudy = useSelector(selectIsStandaloneStudy);
  const studyContext = useSelector(selectStudyContext);
  // Read straight off the context rather than through `useAIDrawer`, which
  // throws outside the provider: study screens are rendered without it in
  // tests, and a missing drawer only means the chat page is the way back.
  const aiDrawer = useContext(AIDrawerContext);

  const isUsingQbank =
    typeof primaryExam?.isUsingQbank === 'boolean'
      ? primaryExam.isUsingQbank
      : todaySession?.isUsingQbank;

  const isUsingFlashcards =
    typeof primaryExam?.isUsingFlashcards === 'boolean'
      ? primaryExam.isUsingFlashcards
      : todaySession?.isUsingFlashcards;

  const isUsingVideos =
    typeof primaryExam?.isUsingVideos === 'boolean'
      ? primaryExam.isUsingVideos
      : todaySession?.isUsingVideos;

  const qbankCount = todaySession?.vignetteCount ?? 0;
  const flashcardCount = todaySession?.flashcardCount ?? 0;
  const videoCount = todaySession?.videoCount ?? 0;

  const qbankCompletedCount =
    activeBlock?.type === BlockType.timed
      ? qbankProgress?.answeredCount || 0
      : qbankProgress?.submittedCount || 0;

  const qbankFinished =
    !isUsingQbank ||
    qbankCount === 0 ||
    Boolean(todaySession?.isComplete) ||
    qbankCompletedCount >= qbankCount;

  const flashcardsFinished =
    !isUsingFlashcards ||
    flashcardCount === 0 ||
    completedFlashcardsCount >= flashcardCount;

  const videosFinished =
    !isUsingVideos || videoCount === 0 || completedVideosCount >= videoCount;

  const sessionFinished = qbankFinished && flashcardsFinished && videosFinished;

  const canShowSessionSummary =
    !isStandaloneStudy &&
    sessionFinished &&
    !isPreclinicalModeExam(primaryExam);

  /**
   * Whether this app instance still holds the thread a set was launched from.
   * Chat state is deliberately not persisted, so a return made in a browser
   * session that only rehydrated the study context has none — which is exactly
   * the case where reopening the drawer left the student on the origin page
   * with no companion over it (staging smoke, 2026-09-17).
   */
  const hasLoadedThread = useSelector((state: RootState) =>
    Boolean(
      selectConversation(state, studyContext.chatReturn?.conversationId ?? null)
    )
  );

  const goHome = useCallback(() => {
    const { chatReturn } = studyContext;

    if (studyContext.returnTo === 'chat' && chatReturn) {
      // Fire and forget: the row the return writes is a nicety, and a student
      // who is on their way back to the thread must not be held at a spinner
      // (or stranded on the study screen) because it failed.
      void returnFromStudySet(chatReturn.setId)
        .then((result) => {
          const eventProps = studySetEventProps(result.set, chatReturn.surface);

          captureStudySetEvent('study_set_return', eventProps);

          // Question sets no longer have a finish screen to report completion
          // from; their normal review exit is the single completion point.
          if (
            result.set.kind === 'question' &&
            result.set.status === 'completed'
          ) {
            captureStudySetEvent('study_set_completed', eventProps);
          }
        })
        .catch(() => undefined);

      // Reopening the companion over the page needs something to reopen: a
      // drawer provider to ask, an account the companion is on for, and the
      // thread itself. Without all three, navigating to the page the student
      // launched from would leave them there with the thread nowhere in sight,
      // so the addressable chat page is the honest way back.
      const destination = resolveStudySetReturn({
        chatReturn,
        returnPath: studyContext.returnPath,
        returnSearch: studyContext.returnSearch,
        canReopenCompanion: Boolean(aiDrawer?.isAvailable) && hasLoadedThread,
      });

      dispatch(exitStandalone());

      navigate(destination.to);

      if (destination.chrome === 'drawer') {
        aiDrawer?.open({
          conversationId: destination.conversationId,
          scrollToBlockId: destination.scrollToBlockId,
        });
      }

      return;
    }

    if (studyContext.returnTo === 'qbank-start') {
      navigate(routes.qbankStart);
      return;
    }
    if (studyContext.returnTo === 'topics') {
      if (studyContext.returnPath) {
        navigate(studyContext.returnPath);
        return;
      }

      const suffix = studyContext.returnSearch
        ? `?${studyContext.returnSearch}`
        : '';

      navigate(`${routes.topics}${suffix}`);
      return;
    }
    if (studyContext.returnTo === 'home') {
      navigate(routes.home);
      return;
    }
    if (studyContext.returnTo === 'documents') {
      // A study session started from a document viewer returns to that
      // document (`returnPath`); with the Documents page retired there is no
      // list to fall back to, so an unknown origin goes home.
      navigate(studyContext.returnPath ?? routes.home);
      return;
    }
    if (studyContext.returnTo === 'video') {
      navigate(studyContext.returnPath ?? '/videos');
      return;
    }
    if (studyContext.returnTo === 'flashcard-decks') {
      navigate(studyContext.returnPath ?? routes.flashcardDecks);
      return;
    }
    if (studyContext.returnTo === 'self-assessments') {
      navigate(studyContext.returnPath ?? routes.selfAssessments);
      return;
    }
    if (studyContext.returnTo === 'faculty-student') {
      navigate(studyContext.returnPath ?? '/faculty');
      return;
    }
    navigate(routes.home);
  }, [
    aiDrawer,
    dispatch,
    hasLoadedThread,
    navigate,
    studyContext.chatReturn,
    studyContext.returnPath,
    studyContext.returnSearch,
    studyContext.returnTo,
  ]);

  const startQbankBlock = useCallback(
    (blockId?: string, preloaded?: boolean) => {
      if (preloaded) {
        resetActiveQuestionIndex();
      }
      const search = buildSearch({ blockId: blockId ?? activeBlock?.id });
      navigate(`${routes.qbankBlock}${search}`);
    },
    [activeBlock?.id, navigate]
  );

  const reviewQbank = useCallback(
    (blockId?: string, preloaded?: boolean) => {
      if (preloaded) {
        resetActiveQuestionIndex();
      }
      const search = buildSearch({ blockId: blockId ?? activeBlock?.id });
      navigate(`${routes.qbankReview}${search}`);
    },
    [activeBlock?.id, navigate]
  );

  const pauseBlock = useCallback(() => {
    if (isStandaloneStudy) {
      goHome();
      return;
    }
    goHome();
  }, [goHome, isStandaloneStudy]);

  const endQbankReview = useCallback(() => {
    resetActiveQuestionIndex();
    if (isStandaloneStudy) {
      goHome();
      return;
    }
    if (canShowSessionSummary) {
      navigate(routes.summary);
      return;
    }
    goHome();
  }, [canShowSessionSummary, goHome, isStandaloneStudy, navigate]);

  const startFlashcards = useCallback(
    (search?: string) => {
      if (search) {
        navigate(`${routes.flashcards}${search}`);
        return;
      }
      if (!flashcardsFinished) {
        navigate(routes.flashcards);
      }
    },
    [flashcardsFinished, navigate]
  );

  const finishFlashcards = useCallback(() => {
    if (canShowSessionSummary) {
      navigate(routes.summary);
      return;
    }
    goHome();
  }, [canShowSessionSummary, goHome, navigate]);

  const startSessionGeneric = useCallback(() => {
    if (!qbankFinished) {
      navigate(routes.qbankBlock);
      return;
    }
    if (!flashcardsFinished) {
      navigate(routes.flashcards);
      return;
    }
    if (!videosFinished) {
      navigate(routes.video);
      return;
    }
    if (canShowSessionSummary) {
      navigate(routes.summary);
      return;
    }
    goHome();
  }, [
    canShowSessionSummary,
    flashcardsFinished,
    goHome,
    navigate,
    qbankFinished,
    videosFinished,
  ]);

  const startVideos = useCallback(() => {
    navigate(routes.video);
  }, [navigate]);

  const startLectureFlashcards = useCallback(
    (lectureIds: string[]) => {
      dispatch(
        enterStandalone({
          blockId: 'flashcards',
          returnTo: 'home',
          flashcardScope: {
            kind: 'lecture',
            lectureIds,
            amount: 'all',
            type: 'both',
          },
        })
      );

      const search = buildLectureSearch(lectureIds);
      navigate(`${routes.flashcards}${search}`);
    },
    [dispatch, navigate]
  );

  const startLectureVideos = useCallback(
    (
      lectureIds: string[],
      returnTo: StudyReturnTo = 'home',
      returnPath: string | null = null
    ) => {
      // Videos run in session mode (no standalone block). Always set an explicit
      // return target (default 'home') so a stale value from a prior session —
      // e.g. a Documents video that set returnTo='documents' — can't misroute
      // this session's exit. returnPath lets a specific screen (a document
      // viewer) be the return target rather than a list.
      dispatch(setReturnTarget({ returnTo, returnPath }));
      const search = buildLectureSearch(lectureIds);
      navigate(`${routes.video}${search}`);
    },
    [dispatch, navigate]
  );

  const sendPhaseEvent = useCallback(
    (event: StudyFlowEvent) => {
      switch (event.type) {
        case 'START_QBANK_BLOCK':
          startQbankBlock(event.blockId, event.preloaded);
          break;
        case 'REVIEW_QBANK':
        case 'REVIEW_QBANK_BLOCK':
          reviewQbank(event.blockId, event.preloaded);
          break;
        case 'PAUSE_BLOCK':
          pauseBlock();
          break;
        case 'END_QBANK_REVIEW':
          endQbankReview();
          break;
        case 'START_FLASHCARDS':
          startFlashcards(event.search);
          break;
        case 'FINISH_FLASHCARDS':
          finishFlashcards();
          break;
        case 'START_SESSION_GENERIC':
          startSessionGeneric();
          break;
        case 'RETURN_TO_HOME':
          goHome();
          break;
        case 'START_VIDEOS':
          startVideos();
          break;
        case 'START_MULTI_LECTURE_FLASHCARDS':
          startLectureFlashcards(event.lectureIds);
          break;
        case 'START_MULTI_LECTURE_VIDEOS':
          startLectureVideos(
            event.lectureIds,
            event.returnTo,
            event.returnPath
          );

          break;
        default:
          break;
      }
    },
    [
      endQbankReview,
      finishFlashcards,
      goHome,
      pauseBlock,
      reviewQbank,
      startFlashcards,
      startLectureFlashcards,
      startLectureVideos,
      startQbankBlock,
      startSessionGeneric,
      startVideos,
    ]
  );

  return {
    sendPhaseEvent,
    startQbankBlock,
    reviewQbank,
    pauseBlock,
    endQbankReview,
    startFlashcards,
    finishFlashcards,
    startSessionGeneric,
    startVideos,
    // No direct callers today, but they back the START_MULTI_LECTURE_* events
    // dispatched through sendPhaseEvent.
    startLectureFlashcards,
    startLectureVideos,
    activeBlock,
  };
};

export type { StudyFlowEvent };
